import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Phone, Clock, BookOpen } from "lucide-react";
import { SITE, SERVICES } from "../lib/site";
import { ARTICLES, ARTICLE_SLUGS } from "../lib/articleContent";

const BASE_URL = "https://alliancecraneservice.com";

export default function Resources() {
  const canonical = `${BASE_URL}/resources`;

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: ARTICLE_SLUGS.map((slug, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${BASE_URL}/resources/${slug}`,
      name: ARTICLES[slug].title,
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Resources", item: canonical },
    ],
  };

  const [featured, ...rest] = ARTICLE_SLUGS.map((s) => ARTICLES[s]);

  return (
    <>
      <Helmet>
        <title>{`Crane Rental Resources & Guides — Central Florida | ${SITE.shortName}`}</title>
        <meta name="description" content="Crane rental guides for Central Florida contractors and property owners — pricing, sizing, HVAC lifts, jobsite prep, truss setting and emergency crane service, from Alliance Crane Service in DeLand, FL." />
        <meta name="keywords" content="crane rental guide, crane rental tips central florida, crane sizing, hvac crane lift guide, crane cost florida" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Crane Rental Resources & Guides — Central Florida" />
        <meta property="og:description" content="Practical crane rental guides for Central Florida contractors and property owners." />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      {/* HERO / PILLAR INTRO */}
      <section className="border-b border-[var(--ac-border)] bg-black">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14 md:py-24">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 border border-[var(--ac-border)] flex items-center justify-center bg-[var(--ac-bg-2)]">
              <BookOpen className="w-5 h-5 text-[var(--ac-red)]" />
            </div>
            <span className="overline">Resources</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            Crane rental guides for Central Florida.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-lg leading-relaxed">
            Straight answers on pricing, crane sizing, HVAC lifts, jobsite prep, truss setting and
            emergency response — written for the contractors and property owners we work with every day.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="btn-primary justify-center" data-testid="resources-cta-quote">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${SITE.phoneE164}`} className="btn-ghost justify-center" data-testid="resources-cta-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* FEATURED ARTICLE */}
      <section className="py-14 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <Link
            to={`/resources/${featured.slug}`}
            data-testid={`article-card-${featured.slug}`}
            className="group grid grid-cols-1 lg:grid-cols-2 border border-[var(--ac-border)] hover-card-border overflow-hidden"
          >
            <div className="aspect-[16/10] lg:aspect-auto overflow-hidden bg-black">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-7 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <span className="kbd-badge">{featured.category}</span>
                <span className="text-xs text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.readTime}</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl leading-tight group-hover:text-[var(--ac-red)] transition-colors">
                {featured.title}
              </h2>
              <p className="mt-4 text-zinc-400 leading-relaxed">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--ac-red)]">
                Read the guide <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ARTICLE GRID */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="overline mb-6">All Guides</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[var(--ac-border)]">
            {rest.map((a) => (
              <Link
                key={a.slug}
                to={`/resources/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="hover-card-border border-b border-r border-[var(--ac-border)] p-7 group bg-[var(--ac-bg)] flex flex-col"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="kbd-badge">{a.category}</span>
                  <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {a.readTime}</span>
                </div>
                <h3 className="font-display text-2xl leading-tight group-hover:text-[var(--ac-red)] transition-colors">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed flex-1">{a.excerpt}</p>
                <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--ac-red)]">
                  Read more <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES LINK BLOCK */}
      <section className="py-14 md:py-20 bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="overline mb-3">Our Services</div>
              <h2 className="font-display text-3xl md:text-4xl">Ready to book the lift?</h2>
            </div>
            <Link to="/services" className="btn-ghost" data-testid="resources-all-services">
              All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 border-t border-l border-[var(--ac-border)]">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`resources-service-${s.slug}`}
                className="hover-card-border border-b border-r border-[var(--ac-border)] p-5 bg-[var(--ac-bg)] font-display text-lg leading-tight hover:text-[var(--ac-red)]"
              >
                {s.title}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
